import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AtecoCombobox } from "@/components/AtecoCombobox";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, Save, MapPin } from "lucide-react";

const EMPTY_SEDE = {
  nome_sede: "", indirizzo: "", comune: "", provincia: "", cap: "",
  referente_sede: "", accessi_annui: 1, km_da_csc: 0, ore_trasferta: 0,
  pedaggi: 0, pasti: 0, note: "",
};

const EMPTY_CLIENT = {
  ragione_sociale: "", partita_iva: "", codice_fiscale: "", codice_sdi: "", pec: "",
  email: "", telefono: "", indirizzo: "", comune: "", provincia: "", cap: "", regione: "",
  numero_dipendenti: 0, codice_ateco: "", settore_attivita: "", descrizione_attivita: "",
  igiene_alimentare: false, piscina_impianti: false, livello_rischio: "basso", note_interne: "",
  tags: [],
  legale_rappresentante: { nome: "", cognome: "", codice_fiscale: "", luogo_nascita: "", data_nascita: "", residenza: "", email: "", telefono: "" },
  referente_operativo: { nome_cognome: "", ruolo: "", email: "", telefono: "" },
  sedi: [],
};

export default function ClientFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = id && id !== "nuovo";
  const [client, setClient] = useState(EMPTY_CLIENT);
  const [ateco, setAteco] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/ateco").then(({ data }) => setAteco(data)).catch(() => {});
    if (isEdit) {
      api.get(`/clients/${id}`).then(({ data }) => setClient({ ...EMPTY_CLIENT, ...data })).catch(() => toast.error("Cliente non trovato"));
    }
  }, [id]);

  const set = (k, v) => setClient((c) => ({ ...c, [k]: v }));
  const setLegale = (k, v) => setClient((c) => ({ ...c, legale_rappresentante: { ...c.legale_rappresentante, [k]: v } }));
  const setRef = (k, v) => setClient((c) => ({ ...c, referente_operativo: { ...c.referente_operativo, [k]: v } }));

  const addSede = () => setClient((c) => ({ ...c, sedi: [...(c.sedi || []), { ...EMPTY_SEDE }] }));
  const updSede = (i, k, v) => setClient((c) => ({ ...c, sedi: c.sedi.map((s, idx) => idx === i ? { ...s, [k]: v } : s) }));
  const rmSede = (i) => setClient((c) => ({ ...c, sedi: c.sedi.filter((_, idx) => idx !== i) }));

  const onAtecoChange = (code) => {
    set("codice_ateco", code);
    const entry = ateco.find((a) => a.codice === code);
    if (entry) set("livello_rischio", entry.rischio);
  };

  const computeKm = async (sedeIdx) => {
    const sede = client.sedi[sedeIdx];
    const dest = `${sede.indirizzo} ${sede.cap} ${sede.comune} ${sede.provincia}`.trim();
    if (!dest) return toast.error("Inserisci l'indirizzo della sede prima");
    try {
      const { data } = await api.post("/maps/distance", { destination: dest });
      if (data.status === "ok") {
        updSede(sedeIdx, "km_da_csc", data.km);
        const costo = data.costo != null ? ` — costo trasferta ${data.costo}€` : "";
        const dur = data.duration_text ? ` (${data.duration_text})` : "";
        toast.success(`A/R ${data.km} km${dur}${costo}`);
      } else if (data.status === "no_api_key") {
        toast.error("Google Maps API key non configurata. Inserimento manuale km.");
      } else if (data.status === "no_origin") {
        toast.error("Sede CSC non impostata in Impostazioni.");
      } else {
        toast.error(data.message || `Impossibile calcolare distanza (${data.status})`);
      }
    } catch (e) {
      toast.error("Errore calcolo distanza");
    }
  };

  const submit = async () => {
    if (!client.ragione_sociale.trim()) return toast.error("Ragione sociale obbligatoria");
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/clients/${id}`, client);
        toast.success("Cliente aggiornato");
      } else {
        const { data } = await api.post("/clients", client);
        toast.success("Cliente creato");
        navigate(`/clienti/${data.id}`);
        return;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 pb-24">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/clienti")} className="p-2 rounded hover:bg-slate-100" data-testid="back-clients">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-mono">
              {isEdit ? "Modifica cliente" : "Nuovo cliente"}
            </div>
            <h1 className="font-display text-2xl font-bold text-[#0F2C59]">
              {client.ragione_sociale || "Nuovo cliente"}
            </h1>
          </div>
        </div>
        <Button onClick={submit} disabled={saving} className="bg-[#0F2C59] hover:bg-[#173F7A]" data-testid="save-client-btn">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Salvataggio…" : "Salva cliente"}
        </Button>
      </div>

      <Tabs defaultValue="azienda">
        <TabsList>
          <TabsTrigger value="azienda" data-testid="tab-azienda">Azienda</TabsTrigger>
          <TabsTrigger value="legale" data-testid="tab-legale">Legale rappresentante</TabsTrigger>
          <TabsTrigger value="referente" data-testid="tab-referente">Referente operativo</TabsTrigger>
          <TabsTrigger value="sedi" data-testid="tab-sedi">Sedi operative ({client.sedi?.length || 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="azienda" className="mt-4">
          <Card className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Ragione sociale *" v={client.ragione_sociale} onC={(v) => set("ragione_sociale", v)} testId="rag-sociale" />
              <Field label="Partita IVA" v={client.partita_iva} onC={(v) => set("partita_iva", v)} />
              <Field label="Codice Fiscale" v={client.codice_fiscale} onC={(v) => set("codice_fiscale", v)} />
              <Field label="Codice SDI" v={client.codice_sdi} onC={(v) => set("codice_sdi", v)} />
              <Field label="PEC" v={client.pec} onC={(v) => set("pec", v)} />
              <Field label="Email" v={client.email} onC={(v) => set("email", v)} />
              <Field label="Telefono" v={client.telefono} onC={(v) => set("telefono", v)} />
              <Field label="Numero dipendenti" type="number" v={client.numero_dipendenti} onC={(v) => set("numero_dipendenti", Number(v))} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2"><Field label="Indirizzo sede legale" v={client.indirizzo} onC={(v) => set("indirizzo", v)} /></div>
              <Field label="CAP" v={client.cap} onC={(v) => set("cap", v)} />
              <Field label="Comune" v={client.comune} onC={(v) => set("comune", v)} />
              <Field label="Provincia" v={client.provincia} onC={(v) => set("provincia", v)} />
              <Field label="Regione" v={client.regione} onC={(v) => set("regione", v)} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div>
                <Label>Codice ATECO</Label>
                <AtecoCombobox
                  items={ateco}
                  value={client.codice_ateco || ""}
                  onChange={(code, entry) => {
                    set("codice_ateco", code);
                    if (entry?.rischio) set("livello_rischio", entry.rischio);
                  }}
                />
              </div>
              <Field label="Settore attività" v={client.settore_attivita} onC={(v) => set("settore_attivita", v)} />
              <div>
                <Label>Livello rischio</Label>
                <Select value={client.livello_rischio} onValueChange={(v) => set("livello_rischio", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="rischio-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basso">Basso</SelectItem>
                    <SelectItem value="medio">Medio</SelectItem>
                    <SelectItem value="alto">Alto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Descrizione attività</Label>
              <Textarea value={client.descrizione_attivita} onChange={(e) => set("descrizione_attivita", e.target.value)} className="mt-1.5" rows={3} />
            </div>
            <div className="flex flex-wrap gap-6 pt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={client.igiene_alimentare} onCheckedChange={(v) => set("igiene_alimentare", !!v)} data-testid="chk-haccp" />
                <span className="text-sm">Igiene alimentare / HACCP</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={client.piscina_impianti} onCheckedChange={(v) => set("piscina_impianti", !!v)} data-testid="chk-piscina" />
                <span className="text-sm">Piscina / impianti tecnologici</span>
              </label>
            </div>
            <div>
              <Label>Note interne</Label>
              <Textarea value={client.note_interne} onChange={(e) => set("note_interne", e.target.value)} className="mt-1.5" rows={2} />
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="legale" className="mt-4">
          <Card className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Nome" v={client.legale_rappresentante.nome} onC={(v) => setLegale("nome", v)} />
            <Field label="Cognome" v={client.legale_rappresentante.cognome} onC={(v) => setLegale("cognome", v)} />
            <Field label="Codice fiscale" v={client.legale_rappresentante.codice_fiscale} onC={(v) => setLegale("codice_fiscale", v)} />
            <Field label="Luogo di nascita" v={client.legale_rappresentante.luogo_nascita} onC={(v) => setLegale("luogo_nascita", v)} />
            <Field label="Data di nascita" type="date" v={client.legale_rappresentante.data_nascita} onC={(v) => setLegale("data_nascita", v)} />
            <Field label="Residenza" v={client.legale_rappresentante.residenza} onC={(v) => setLegale("residenza", v)} />
            <Field label="Email" v={client.legale_rappresentante.email} onC={(v) => setLegale("email", v)} />
            <Field label="Telefono" v={client.legale_rappresentante.telefono} onC={(v) => setLegale("telefono", v)} />
          </Card>
        </TabsContent>

        <TabsContent value="referente" className="mt-4">
          <Card className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Nome e cognome" v={client.referente_operativo.nome_cognome} onC={(v) => setRef("nome_cognome", v)} />
            <Field label="Ruolo" v={client.referente_operativo.ruolo} onC={(v) => setRef("ruolo", v)} />
            <Field label="Email" v={client.referente_operativo.email} onC={(v) => setRef("email", v)} />
            <Field label="Telefono" v={client.referente_operativo.telefono} onC={(v) => setRef("telefono", v)} />
          </Card>
        </TabsContent>

        <TabsContent value="sedi" className="mt-4 space-y-4">
          {(client.sedi || []).map((s, i) => (
            <Card key={i} className="p-5">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#0F2C59]" />
                  <span className="font-display font-semibold text-[#0F2C59]">Sede #{i + 1}</span>
                </div>
                <Button variant="outline" size="sm" onClick={() => rmSede(i)} data-testid={`rm-sede-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Nome sede" v={s.nome_sede} onC={(v) => updSede(i, "nome_sede", v)} />
                <Field label="Referente" v={s.referente_sede} onC={(v) => updSede(i, "referente_sede", v)} />
                <Field label="Accessi/anno" type="number" v={s.accessi_annui} onC={(v) => updSede(i, "accessi_annui", Number(v))} />
                <div className="md:col-span-2"><Field label="Indirizzo" v={s.indirizzo} onC={(v) => updSede(i, "indirizzo", v)} /></div>
                <Field label="CAP" v={s.cap} onC={(v) => updSede(i, "cap", v)} />
                <Field label="Comune" v={s.comune} onC={(v) => updSede(i, "comune", v)} />
                <Field label="Provincia" v={s.provincia} onC={(v) => updSede(i, "provincia", v)} />
                <div className="flex items-end gap-2">
                  <div className="flex-1"><Field label="Km da CSC (A/R)" type="number" v={s.km_da_csc} onC={(v) => updSede(i, "km_da_csc", Number(v))} /></div>
                  <Button variant="outline" onClick={() => computeKm(i)} data-testid={`compute-km-${i}`}>Calcola</Button>
                </div>
                <Field label="Ore trasferta" type="number" v={s.ore_trasferta} onC={(v) => updSede(i, "ore_trasferta", Number(v))} />
                <Field label="Pedaggi (€)" type="number" v={s.pedaggi} onC={(v) => updSede(i, "pedaggi", Number(v))} />
                <Field label="Pasti previsti" type="number" v={s.pasti} onC={(v) => updSede(i, "pasti", Number(v))} />
              </div>
            </Card>
          ))}
          <Button onClick={addSede} variant="outline" data-testid="add-sede-btn">
            <Plus className="w-4 h-4 mr-1" /> Aggiungi sede operativa
          </Button>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, v, onC, type = "text", testId }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input
        type={type}
        value={v ?? ""}
        onChange={(e) => onC(e.target.value)}
        className="mt-1.5"
        data-testid={testId || `field-${label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
      />
    </div>
  );
}
